import React, { type FC } from 'react'
import { Dialog, DialogActions, DialogTitle, Grid, LinearProgress, type SxProps, type Theme } from '@mui/material'

import type { ReadonlyRecord } from '../../../../utils'

import { CommissioningFileParsingError, ConnectwareServiceCreationError, Translation } from '../../../../domain'

import { Button } from '../../common'
import { ErrorMessage } from '../../ErrorMessage'
import { FormattedTranslation, useTranslator } from '../../Internationalization'
import { ConfigurationSection } from './ConfigurationSection'
import { type ModalConfig, ModalConfigProvider } from './Context'
import { FileLabel } from './FileLabel'
import { FileUploadSection } from './FileUpload'
import { GenericError } from './GenericError'
import {
    useCancel,
    useConfirm,
    useConfirmationButtonText,
    useDismissalButtonText,
    useError,
    useId,
    useIsDisabled,
    useIsOpen,
    useRequesting,
    useTitle,
} from './Hooks'
import { ParsingError } from './ParsingError'

const footerStyle: SxProps<Theme> = { display: 'flex', pl: 6, '& > *': { my: 'auto' } }
const actionsStyle: SxProps<Theme> = { ml: 'auto' }

const InternalModal: FC = () => {
    const translator = useTranslator()

    const open = useIsOpen()
    const title = useTitle()
    const requesting = useRequesting()
    const error = useError()
    const id = useId()
    const cancel = useCancel()
    const isDisabled = useIsDisabled()
    const confirm = useConfirm()
    const confirmationButtonText = useConfirmationButtonText()
    const reversalButtonText = useDismissalButtonText()

    if (!open) {
        return null
    }

    return (
        <Dialog data-testid="add-or-update-modal" maxWidth="lg" fullWidth disableEscapeKeyDown open>
            <Grid container>
                {/* Title */}
                <Grid size={{ xs: 12 }}>
                    <DialogTitle>
                        <FormattedTranslation id={title} />
                    </DialogTitle>
                    <LinearProgress variant={requesting === true ? 'indeterminate' : 'determinate'} value={0} />
                </Grid>
                {/* Main */}
                <Grid size={{ xs: 5 }}>
                    <FileUploadSection />
                    <FileLabel />
                </Grid>
                <Grid size={{ xs: 7 }}>
                    <ConfigurationSection />
                </Grid>
                {/* Error / Actions */}
                <Grid data-testid="modal-footer" sx={footerStyle} size={{ xs: 12 }}>
                    {error && (
                        <ErrorMessage
                            data-testid="error-column"
                            error={error}
                            titleVariant="body2"
                            customTitle={(e) =>
                                CommissioningFileParsingError.is(e)
                                    ? <ParsingError error={e} />
                                    : ConnectwareServiceCreationError.is(e)
                                    ? <FormattedTranslation id={e.translation} values={{ id }} />
                                    : <GenericError error={e} />}
                        />
                    )}
                    <DialogActions sx={actionsStyle}>
                        <Button
                            data-testid="add-or-update-modal-cancel"
                            aria-label={translator.formatTranslation(reversalButtonText)}
                            variant="secondary"
                            onClick={cancel}
                        >
                            <FormattedTranslation id={reversalButtonText} />
                        </Button>
                        {confirmationButtonText && (
                            <Button
                                data-testid="add-or-update-modal-confirm"
                                aria-label={translator.formatTranslation(confirmationButtonText)}
                                variant="primary"
                                disabled={isDisabled}
                                onClick={confirm}
                            >
                                <FormattedTranslation id={confirmationButtonText} />
                            </Button>
                        )}
                    </DialogActions>
                </Grid>
            </Grid>
        </Dialog>
    )
}

const config: ReadonlyRecord<'CREATE' | 'EDIT' | 'VIEW', ModalConfig> = {
    CREATE: {
        title: Translation.SERVICE_INSTALL,
        configurationTitle: Translation.SERVICE_CONFIGURE_NEW,
        confirmationButtonText: Translation.INSTALL,
        dismissalButtonText: Translation.CANCEL,
        noConfigurationText: Translation.NOTHING_TO_CONFIGURE,
        usecase: 'addServiceUsecase',
        shouldShowUploadInstructions: true,
        hasNumberedSteps: true,
        isEditable: true,
    },
    EDIT: {
        title: Translation.SERVICE_UPDATE_RECONFIGURE,
        configurationTitle: Translation.SERVICE_CONFIGURE_CURRENT,
        confirmationButtonText: Translation.UPDATE,
        dismissalButtonText: Translation.CANCEL,
        noConfigurationText: Translation.NOTHING_TO_CONFIGURE,
        usecase: 'updateServiceUsecase',
        shouldShowUploadInstructions: true,
        hasNumberedSteps: true,
        isEditable: true,
    },
    VIEW: {
        title: Translation.SERVICE_CONFIGURATION,
        configurationTitle: Translation.SERVICE_CONFIGURATION,
        confirmationButtonText: null,
        dismissalButtonText: Translation.CLOSE,
        noConfigurationText: Translation.NO_CONFIGURATION,
        usecase: 'updateServiceUsecase',
        shouldShowUploadInstructions: false,
        hasNumberedSteps: false,
        isEditable: false,
    },
}

export const AddOrUpdateModal: FC<{ mode: keyof typeof config } & Pick<ModalConfig, 'onConfirmed'>> = ({ mode, onConfirmed }) => (
    <ModalConfigProvider value={{ ...config[mode], onConfirmed }}>
        <InternalModal />
    </ModalConfigProvider>
)
